import { create, el } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { locate, locateNodeByClassName } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { extend } from "@dhx/ts-common/core";
import { HexToHSV, isHex } from "./helpers/color";
import { grayShades, palette } from "./colors";
import locale from "./locales/en";
import {
	ColorpickerEvents,
	IColorpicker,
	IColorpickerConfig,
	IPickerState,
	ViewsMode,
	IEventHandlersMap,
} from "./types";
// tslint:disable-next-line
import { tooltip } from "@dhx/ts-message/sources/tooltip";
// tslint:disable-next-line
import { Position as TooltipPosition } from "@dhx/ts-message/";
import { getPicker } from "./picker";
import { calculateRangeGrip, calculatePaletteGrip } from "./helpers/calculations";
import { IHandlers } from "@dhx/ts-common/types";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";
import { focusManager } from "@dhx/ts-common/FocusManager";
import { rgbToHex } from "@dhx/ts-common/core";

export class Colorpicker extends View implements IColorpicker {
	config: IColorpickerConfig;
	events: IEventSystem<ColorpickerEvents, IEventHandlersMap>;
	private _keyManager: IKeyManager;
	private _selected: string;
	private _handlers: IHandlers;
	private _pickerState: IPickerState;
	private _inputTimeout: any;

	constructor(container: string | HTMLElement, config?: IColorpickerConfig) {
		super(container, config);
		this._container = container;
		this.config = extend(
			({
				css: "",
				grayShades: true,
				pickerOnly: false,
				paletteOnly: false,
				customColors: [],
				palette,
				width: "238px",
				mode: "palette",
			} as any) as IColorpickerConfig,
			this.config
		);
		if (!this.config.palette) {
			this.config.palette = palette;
		}
		if (this.config.customColors) {
			this.config.customColors = this.config.customColors.map(color => color.toUpperCase());
		}
		this._pickerState = {
			hsv: { h: 0, s: 1, v: 1 },
			customHex: "",
		};

		this.events = new EventSystem(this);
		this._setHandlers();

		this._keyManager = new KeyManager((e: Event, focusId: string) => focusId === this._uid);
		this._initHotKey();

		const view = create({ render: () => this._getContent() });
		this.mount(this._container, view);
	}
	destructor(): void {
		this.events && this.events.clear();

		this.config = this.events = this._selected = this._handlers = this._pickerState = this._inputTimeout = null;
		this.unmount();
	}
	clear(): void {
		this._selected = "";
		this.events.fire(ColorpickerEvents.change, [this._selected]);
		this.paint();
	}
	setValue(value: string): void {
		if (this._focusColor(value) || focusManager.getFocusId() === this._uid) {
			this.paint();
			this.events.fire(ColorpickerEvents.change, [this._selected]);
			this.events.fire(ColorpickerEvents.colorChange, [this._selected]); // TODO: remove suite_7.0
		}
	}
	setFocus(value: string): void {
		if (this._focusColor(value)) {
			this.paint();
		}
	}
	getValue(): string {
		return this._selected || "";
	}
	getCustomColors(): string[] {
		return this.config.customColors;
	}
	setCustomColors(customColors: string[]): void {
		this.config.customColors = customColors.map(color => color.toUpperCase());
		this.paint();
	}
	setCurrentMode(mode: ViewsMode): void {
		if (mode === "palette" || mode === "picker") {
			this.config.mode = mode;
			this.events.fire(ColorpickerEvents.modeChange, [mode]);
			this.events.fire(ColorpickerEvents.viewChange, [mode]); // TODO: remove suite_7.0
			this.paint();
		}
	}
	getCurrentMode(): ViewsMode {
		return this.config.mode;
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	getView(): ViewsMode {
		return this.getCurrentMode();
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	setView(mode: ViewsMode): void {
		this.setCurrentMode(mode);
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	focusValue(value: string): void {
		this.setFocus(value);
	}
	private _setHandlers(): void {
		this._handlers = {
			click: {
				".dhx_palette__cell": this._onColorClick,
			},
			mousedown: (e: MouseEvent) => {
				this._pickerMove(e);
			},
			touchstart: (e: TouchEvent) => {
				this._pickerMove(e);
			},
			buttonsClick: (button: "cancel" | "apply") => {
				this.setCurrentMode("palette");

				if (button === "cancel") {
					this.events.fire(ColorpickerEvents.cancelClick, []);
					return;
				}
				if (button === "apply" && !this.config.customColors.includes(this._pickerState.background)) {
					this.setValue(this._pickerState.background);
					this.events.fire(ColorpickerEvents.apply, []);
					this.events.fire(ColorpickerEvents.selectClick, []); // TODO: remove suite_7.0
				}
			},
			customColorClick: () => {
				this.setView("picker");
			},
			oninput: (e: any) => {
				if (this._inputTimeout) {
					clearTimeout(this._inputTimeout);
				}
				this._inputTimeout = setTimeout(() => {
					let val = e.target.value;
					if (val.indexOf("#") === -1) {
						val = `#${val}`;
					}
					this._pickerState.customHex = val;
					if (isHex(val)) {
						this._pickerState.hsv = HexToHSV(val);
						this.paint();
					}
				}, 100);
			},
			contextmenu: {
				".dhx_palette__cell": (e, node) => {
					e.preventDefault();
					const index = this.config.customColors.indexOf(node.data.color);
					if (index !== -1) {
						this._removeCustomColor(index);
					}
					this.paint();
					return;
				},
			},
			mouseover: {
				".dhx_palette__cell": (e: any) => {
					if (e.target) {
						tooltip(locale.rightClickToDelete, {
							node: e.target,
							position: TooltipPosition.bottom,
						});
					}
				},
				".dhx_colorpicker-custom-colors__picker": (e: any) => {
					if (e.target) {
						tooltip(locale.addNewColor, {
							node: e.target,
							position: TooltipPosition.bottom,
						});
					}
				},
			},
		};
		this.events.on(ColorpickerEvents.change, () => {
			this.paint();
		});
		// TODO: remove suite_7.0
		this.events.on(ColorpickerEvents.colorChange, () => {
			this.paint();
		});
	}
	private _pickerMove(e: any): void {
		const name = locate(e);
		this._pickerState.customHex = "";

		if (name === "picker_palette") {
			this._setPaletteGrip(e);
		} else {
			this._setRangeGrip(e);
		}
		const handler = name === "picker_palette" ? this._setPaletteGrip : this._setRangeGrip;

		const move = e.targetTouches ? "touchmove" : "mousemove";
		const up = e.targetTouches ? "touchend" : "mouseup";

		document.addEventListener(move, handler);
		document.addEventListener(up, () => {
			document.removeEventListener(move, handler);
		});
		this.paint();
	}
	private _focusColor(value: string): boolean {
		if (value === undefined || value.length < 4) {
			return false;
		}
		const hex = value.toUpperCase();
		if (!isHex(hex)) {
			return false;
		}
		const isInPalette = this.config.palette.reduce((state, col) => {
			if (state) {
				return state;
			}
			col.forEach(color => {
				if (color.toUpperCase() === hex) {
					state = true;
					return;
				}
			});
			return state;
		}, false);
		const isInGrayShades = grayShades.includes(hex);
		if (!isInPalette && !isInGrayShades) {
			const colors = this.getCustomColors();
			if (!colors.includes(hex.toUpperCase())) {
				colors.push(hex.toUpperCase());
			}
		}
		this._selected = hex || null;
		this._pickerState.hsv = HexToHSV(hex);
		return true;
	}
	private _setPaletteGrip = e => {
		const paletteRect = this.getRootView().refs.picker_palette.el.getBoundingClientRect();

		const clientX = e.targetTouches ? e.targetTouches[0].clientX : e.clientX;
		const clientY = e.targetTouches ? e.targetTouches[0].clientY : e.clientY;

		const top = clientY - paletteRect.top;
		const left = clientX - paletteRect.left;
		const { s, v } = calculatePaletteGrip(paletteRect, top, left);
		this._pickerState.hsv.s = s;
		this._pickerState.hsv.v = v;
		this.paint();
	};
	private _setRangeGrip = e => {
		const rangeRect = this.getRootView().refs.hue_range.el.getBoundingClientRect();
		const clientX = e.targetTouches ? e.targetTouches[0].clientX : e.clientX;
		const left = clientX - rangeRect.left;

		const { h, rangeLeft } = calculateRangeGrip(rangeRect, left);
		this._pickerState.hsv.h = h;
		this._pickerState.rangeLeft = rangeLeft;
		this.paint();
	};
	private _onColorClick = (_e, node) => {
		this._selected = node.data.color.toUpperCase();
		this.events.fire(ColorpickerEvents.change, [this._selected]);
		this.events.fire(ColorpickerEvents.colorChange, [this._selected]); // TODO: remove suite_7.0
	};
	private _removeCustomColor(index: number) {
		this.config.customColors.splice(index, 1);
	}
	private _getCells(colors: any[], cellClass = ""): any[] {
		return colors.reduce((cells, color) => {
			const selected =
				(this._selected || "").toUpperCase() === color.toUpperCase()
					? "dhx_palette__cell--selected"
					: "";
			cells.push(
				el(".dhx_palette__cell", {
					class: `${selected} ${cellClass}`,
					_data: { color },
					style: `background:${color}`,
					tabindex: 0,
				})
			);
			return cells;
		}, []);
	}
	private _getGrayShades(): any {
		return el(".dhx_palette__row", this._getCells(grayShades));
	}
	private _getPalette(): any[] {
		return this.config.palette.reduce((total, row) => {
			total.push(el(".dhx_palette__col", this._getCells(row)));
			return total;
		}, []);
	}
	private _getContent() {
		let view: any[];
		if (this.config.pickerOnly) {
			view = [getPicker(this, this._pickerState, this._handlers)];
		} else {
			view =
				this.config.mode === "palette"
					? [
							this.config.grayShades && this._getGrayShades(),
							...this._getPalette(),
							!this.config.paletteOnly &&
								el(
									".dhx_colorpicker-custom-colors",
									{
										onmouseover: this._handlers.mouseover,
									},
									[
										el(".dhx_colorpicker-custom-colors__header", [locale.customColors]),
										el(".dhx_palette--custom.dhx_palette__row", [
											...this._getCells(
												this.config.customColors,
												"dhx_custom-color__cell"
											),
											el(".dhx_colorpicker-custom-colors__picker", {
												class: "dxi dxi-plus",
												onclick: this._handlers.customColorClick,
												onmouseover: this._handlers.mouseover,
												tabindex: 0,
											}),
										]),
									]
								),
					  ]
					: [getPicker(this, this._pickerState, this._handlers)];
		}

		return el(
			".dhx_widget.dhx_colorpicker",
			{
				class: this.config.css,
				style: { width: this.config.width },
				"data-dhx-widget-id": this._uid,
			},
			[
				el(
					".dhx_palette",
					{
						onclick: this._handlers.click,
						oncontextmenu: this._handlers.contextmenu,
					},
					view
				),
			]
		);
	}
	private _initHotKey() {
		const handlers = {
			enter: e => {
				if (locateNodeByClassName(e, "dhx_palette__cell")) {
					this._selected = rgbToHex(e.target.style.background);
					this.events.fire(ColorpickerEvents.change, [this._selected]);
					this.events.fire(ColorpickerEvents.colorChange, [this._selected]); // TODO: remove suite_7.0
				}
				if (locateNodeByClassName(e, "dhx_colorpicker-custom-colors__picker")) {
					this.setCurrentMode("picker");
				}
			},
		};

		for (const key in handlers) {
			this._keyManager.addHotKey(key, handlers[key]);
		}
	}
}
